export class TaskTracker {
    public count = 0;

    public resolvedCount = 0;

    public rejectedCount = 0;

    public runningCount = 0;

    public constructor(private progressListener: (tasks: TaskTracker) => void) {}

    public createTask<TInput, TOutput>(
        action: (input?: TInput) => Promise<TOutput> | TOutput,
    ): () => Promise<TOutput> {
        this.count++;

        return async () => {
            this.runningCount++;
            try {
                const output = await action();
                this.runningCount--;
                this.resolvedCount++;
                this.progressListener(this);
                return output;
            } catch (err) {
                this.runningCount--;
                this.rejectedCount++;
                this.progressListener(this);
                throw err;
            }
        };
    }
}

export abstract class PromiseLibrary {
    public abstract create<T>(
        callback: (resolve: (value?: T) => any, reject: (err?: any) => any) => any,
    ): PromiseLike<T>;

    public try<T>(action: () => T): PromiseLike<T> {
        return this.create((resolve, reject) => {
            try {
                const result = action();
                resolve(result);
            } catch (err) {
                reject(err);
            }
        });
    }

    public abstract all<T>(promises: Array<PromiseLike<T>>): PromiseLike<T[]>;

    public allActions<T>(actions: Array<() => PromiseLike<T>>): () => PromiseLike<T[]> {
        return () => {
            try {
                const promises: Array<PromiseLike<T>> = [];
                for (const action of actions) {
                    promises.push(action());
                }
                return this.all(promises);
            } catch (err) {
                return this.reject(err);
            }
        };
    }

    public allInSerial<T>(actions: Array<() => PromiseLike<T>>): PromiseLike<T[]> {
        const results: T[] = [];
        let result = this.resolve();

        for (const action of actions) {
            result = result.then(() =>
                // eslint-disable-next-line promise/no-nesting
                action().then(value => {
                    results.push(value);
                    return value;
                }),
            );
        }

        return result.then(() => results);
    }

    public resolve<T>(value?: T): PromiseLike<T> {
        return this.create(resolve => resolve(value));
    }

    public reject(err?: any): PromiseLike<any> {
        return this.create((_, reject) => reject(err));
    }
}

export function isPromise(value: any): value is PromiseLike<any> {
    return typeof value.then === 'function';
}
